import { useEffect, useMemo, useState } from 'react';

import { type ParseHeaderSampleResponse } from '../../../../../../../../../../backend/src/integrations/relativity/load-file-import/load-file-import-types';
import { buildFilenameKeyMap, columnHasDuplicates, computeColumnMatch, sampleValuesFor } from '../helpers';
import type { WizardState } from '../import-wizard';
import useGetDocumentFilenames from '@/api/queries/useGetDocumentFilenames';
import { Icon } from '@/new/components/icon';
import { Input } from '@/new/components/shadcn/input';
import { RadioGroup, RadioGroupItem } from '@/new/components/shadcn/radio-group';
import { Switch } from '@/new/components/shadcn/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { TooltipIcon } from '@/new/components/tooltip-icon';
import { cn } from '@/new/lib/utils';

interface FilenameStepProps extends Pick<WizardState, 'filenameColumnHeader' | 'matchSettings'> {
  matterId: string;
  parseResult: ParseHeaderSampleResponse;
  /** Name of the uploaded load file, shown in the preview header. */
  fileName?: string;
  onChange: (partial: Partial<WizardState>) => void;
}

/** How many sample rows to show in the top preview table. */
const PREVIEW_ROWS = 3;

const DUPLICATE_HINT = "Column has duplicate values, so it can't uniquely identify a document.";

export function FilenameStep({
  matterId,
  parseResult,
  fileName,
  filenameColumnHeader,
  matchSettings,
  onChange,
}: FilenameStepProps) {
  const [query, setQuery] = useState('');
  const { data: documentFilenames, isLoading: isLoadingFilenames } = useGetDocumentFilenames(matterId);

  // Normalised Wexler filename index (key -> original name). Rebuilds as the
  // Wexler-side "ignore file extensions" toggle changes.
  const filenameKeyMap = useMemo(
    () =>
      buildFilenameKeyMap(
        (documentFilenames ?? []).map((f) => f.name),
        matchSettings.stripCurrentExtensions,
      ),
    [documentFilenames, matchSettings.stripCurrentExtensions],
  );

  // Headers whose sampled values contain duplicates — can't be an identifier.
  // Independent of any toggle, so this never flips.
  const duplicateHeaders = useMemo(() => {
    const set = new Set<string>();
    parseResult.headers.forEach((header, index) => {
      if (columnHasDuplicates(sampleValuesFor(parseResult, index))) set.add(header);
    });
    return set;
  }, [parseResult]);

  // Per-column match detail keyed by header, recomputed as the filename index changes.
  const columnMatches = useMemo(() => {
    const map = new Map<string, ReturnType<typeof computeColumnMatch>>();
    parseResult.headers.forEach((header, index) => {
      map.set(
        header,
        computeColumnMatch(sampleValuesFor(parseResult, index), filenameKeyMap, { stripIncomingExtensions: false }),
      );
    });
    return map;
  }, [parseResult, filenameKeyMap]);

  const selectColumn = (header: string) => {
    onChange({ filenameColumnHeader: header });
  };

  // Once filenames have loaded, pre-select the first column that can be an
  // identifier (skipping duplicate-valued columns) so the match columns fill in.
  useEffect(() => {
    if (filenameColumnHeader !== null || isLoadingFilenames) return;
    const firstSelectable = parseResult.headers.find((header) => !duplicateHeaders.has(header));
    if (firstSelectable) selectColumn(firstSelectable);
  }, [filenameColumnHeader, isLoadingFilenames, parseResult, duplicateHeaders]);

  const previewRows = useMemo(() => parseResult.sampleRows.slice(0, PREVIEW_ROWS), [parseResult]);
  const selectedMatch = filenameColumnHeader ? columnMatches.get(filenameColumnHeader) : undefined;

  const filteredHeaders = useMemo(
    () => parseResult.headers.filter((h) => h.toLowerCase().includes(query.toLowerCase())),
    [parseResult.headers, query],
  );

  const sampleValues = selectedMatch?.values ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col gap-8 pb-1">
      {isLoadingFilenames && (
        <div className="flex shrink-0 items-center gap-2 text-sm text-gray-400">
          <Icon className="lucide--loader animate-spin ease-in-out" />
          Loading document filenames...
        </div>
      )}

      {/* Identifier picker: column list + its sample values + matched files, side by side */}
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="mb-4 space-y-0.25 px-2">
          <h2 className="text-base">Select Identifier Field</h2>
          <p className="text-gray-500">
            Select the column from the uploaded load file to use to match against filenames in Wexler.
          </p>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-3 gap-4">
          {/* Column names, radio-selectable, searchable */}
          <div className="relative flex h-full min-h-0 flex-col">
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-md border">
              <div className="shrink-0 border-b bg-gray-50 p-2">
                <div className="relative">
                  <Icon className="lucide--search pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search columns..."
                    className="h-8 pl-8"
                  />
                </div>
              </div>
              <RadioGroup
                value={filenameColumnHeader ?? undefined}
                onValueChange={selectColumn}
                className={cn(
                  'flex-1 gap-0 divide-y divide-gray-100 overflow-y-auto',
                  filteredHeaders.length === 0 ? 'flex items-center justify-center' : 'content-start',
                )}
              >
                {filteredHeaders.length === 0 ? (
                  <div className="mb-1 text-sm text-gray-400">No columns found</div>
                ) : (
                  filteredHeaders.map((header) => {
                    const isDuplicate = duplicateHeaders.has(header);
                    const rowClass = cn(
                      'flex items-center gap-2.5 px-3 py-2 text-sm',
                      isDuplicate ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-50',
                    );
                    const inner = (
                      <>
                        <RadioGroupItem value={header} disabled={isDuplicate} />
                        <span className="truncate text-gray-700">{header}</span>
                      </>
                    );
                    return isDuplicate ? (
                      <Tooltip key={header}>
                        <TooltipTrigger asChild>
                          <div className={rowClass}>{inner}</div>
                        </TooltipTrigger>
                        <TooltipContent align="start">{DUPLICATE_HINT}</TooltipContent>
                      </Tooltip>
                    ) : (
                      <div key={header} className={rowClass} onClick={() => selectColumn(header)}>
                        {inner}
                      </div>
                    );
                  })
                )}
              </RadioGroup>
            </div>
          </div>

          {/* Sample values from the .dat file paired with the Wexler file each matched */}
          <div className="col-span-2 flex h-full min-h-0 flex-col">
            <div className="flex items-center gap-3 px-1 pt-1.5 pb-3">
              <p>Matched values from 50 sample rows ({selectedMatch?.matched ?? 0}/50)</p>
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <Switch
                  className="scale-80"
                  checked={matchSettings.stripCurrentExtensions}
                  onCheckedChange={(v) => onChange({ matchSettings: { ...matchSettings, stripCurrentExtensions: v } })}
                />
                <span>Ignore file extensions</span>
                <TooltipIcon tooltipContent="Strip file extensions from filenames in Wexler when matching" />
              </label>
            </div>

            {sampleValues.length === 0 ? (
              <div className="flex items-center gap-2 px-1 text-gray-400">
                <Icon className="lucide--alert-circle" /> No sample rows to preview.
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-auto rounded-md border">
                <Table className="table-fixed">
                  <TableHeader className="sticky top-0 z-10 bg-gray-50">
                    <TableRow>
                      <TableHead className="h-auto border-r px-3 py-2 text-xs font-medium">
                        Sample value from load file
                      </TableHead>
                      <TableHead className="h-auto px-3 py-2 text-xs font-medium">Matching file in Wexler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sampleValues.map((v, i) => (
                      <TableRow key={i}>
                        <TableCell className="border-r px-3 py-2 text-sm">
                          <div className="flex items-center gap-2.5">
                            <Icon
                              className={cn(
                                'shrink-0',
                                v.isMatched ? 'lucide--check text-green-500' : 'lucide--minus text-gray-300',
                              )}
                            />
                            {v.isEmpty ? <p className="text-gray-400">No value</p> : <TruncatedCell value={v.raw} />}
                          </div>
                        </TableCell>
                        <TableCell className="px-3 py-2 text-sm">
                          {v.matchedFilename ? (
                            <TruncatedCell value={v.matchedFilename} />
                          ) : (
                            <p className="text-gray-400">Unmatched</p>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* File preview below the identifier picker */}
      <div className="flex shrink-0 flex-col">
        <div className="mb-4 space-y-0.25 px-2">
          <h2 className="text-base">{fileName && <span>{fileName} </span>}Preview</h2>
          <p className="text-gray-500">A preview of the extracted rows from the load file.</p>
        </div>

        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="sticky left-0 z-10 h-auto w-0 bg-gray-50 px-3 py-2 shadow-[inset_-1px_0_0_0_#e5e7eb]" />
                {parseResult.headers.map((header) => {
                  const isDuplicate = duplicateHeaders.has(header);
                  return (
                    <TableHead
                      key={header}
                      className={cn(
                        'h-auto border-r px-3 py-2 whitespace-nowrap last:border-r-0',
                        isDuplicate && 'opacity-50',
                      )}
                    >
                      {isDuplicate ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center gap-1.5">
                              <Icon className="lucide--alert-circle shrink-0 text-red-500" />
                              {header}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>This column contains duplicate rows</TooltipContent>
                        </Tooltip>
                      ) : (
                        header
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {previewRows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  <TableCell className="sticky left-0 z-10 w-0 bg-gray-50 px-3 py-2 text-center text-xs text-gray-400 tabular-nums shadow-[inset_-1px_0_0_0_#e5e7eb]">
                    {rowIndex + 1}
                  </TableCell>
                  {parseResult.headers.map((header, colIndex) => (
                    <TableCell
                      key={header}
                      className={cn(
                        'border-r px-3 py-2 text-xs last:border-r-0',
                        duplicateHeaders.has(header) && 'opacity-40',
                      )}
                    >
                      <TruncatedCell value={row[colIndex] ?? ''} className="max-w-[240px] text-gray-600" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

/** A sample value clamped to its cell width, with the full value on hover. */
function TruncatedCell({ value, className }: { value: string; className?: string }) {
  if (!value) return <span className="text-gray-400">—</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('block truncate text-gray-700', className)}>{value}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-2xl break-all" align="start">
        {value}
      </TooltipContent>
    </Tooltip>
  );
}
